import React, { useMemo, useCallback } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { $getRoot, $createTextNode, $createParagraphNode, type EditorState } from 'lexical';
import { invoiceEditorConfig, invoiceReadOnlyConfig } from './invoiceEditorConfig';
import InvoiceToolbarPlugin from './InvoiceToolbarPlugin';

interface InvoiceMemoEditorProps {
  initialJson?: Record<string, unknown> | string | null;
  initialText?: string;
  onJsonChange?: (json: Record<string, unknown>) => void;
  onTextChange?: (text: string) => void;
  readOnly?: boolean;
  placeholder?: string;
}

const InvoiceMemoEditor: React.FC<InvoiceMemoEditorProps> = ({
  initialJson,
  initialText,
  onJsonChange,
  onTextChange,
  readOnly = false,
  placeholder = 'Enter invoice description, notes, or terms...',
}) => {
  const editorConfig = useMemo(() => {
    const base = readOnly ? { ...invoiceReadOnlyConfig } : { ...invoiceEditorConfig };

    // Initialize from Lexical JSON if available
    if (initialJson) {
      try {
        const json = typeof initialJson === 'string' ? JSON.parse(initialJson) : initialJson;
        // Lexical accepts a JSON string for initialConfig.editorState
        if (json && json.root) {
          base.editorState = JSON.stringify(json);
        }
      } catch {
        // Invalid JSON, fall through to text initialization
      }
    }

    // Fallback: initialize from plain text
    if (!base.editorState && initialText) {
      base.editorState = () => {
        const root = $getRoot();
        if (root.isEmpty()) {
          const paragraph = $createParagraphNode();
          paragraph.append($createTextNode(initialText));
          root.append(paragraph);
        }
      };
    }

    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only compute once on mount — use key prop to force remount

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const root = $getRoot();
        onTextChange?.(root.getTextContent());
      });
      onJsonChange?.(editorState.toJSON() as unknown as Record<string, unknown>);
    },
    [onJsonChange, onTextChange]
  );

  return (
    <div className="invoice-memo-editor">
      <LexicalComposer initialConfig={editorConfig}>
        <InvoiceToolbarPlugin readOnly={readOnly} />
        <div className={`relative border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 ${readOnly ? 'rounded-lg' : 'rounded-b-lg'}`}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={`outline-none px-3 py-2 text-gray-900 dark:text-white text-sm min-h-[150px] ${
                  readOnly ? 'cursor-default' : 'cursor-text'
                }`}
              />
            }
            placeholder={
              !readOnly ? (
                <div className="absolute top-2 left-3 text-gray-400 dark:text-gray-500 pointer-events-none select-none text-sm">
                  {placeholder}
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          {!readOnly && <OnChangePlugin onChange={handleChange} />}
        </div>
      </LexicalComposer>
    </div>
  );
};

export default InvoiceMemoEditor;
